/** Maps notification types to in-app navigation links based on notification metadata. */
import type { Notification } from '@formency/shared';

const GENERATION_TYPES = [
  'generation_awaiting_approval',
  'generation_complete',
  'generation_failed',
  'generation_cancelled',
] as const;

/**
 * Compute a navigation link for a notification based on its type and metadata.
 * Returns null if the notification has no meaningful destination.
 */
export function getNotificationLink(notification: Notification): string | null {
  if (GENERATION_TYPES.includes(notification.type as (typeof GENERATION_TYPES)[number])) {
    const metadata = notification.metadata as Record<string, unknown> | null;
    if (!metadata) return null;

    const scope = metadata.scope as string | undefined;
    const courseId = metadata.courseId as string | undefined;

    if (scope === 'course') {
      return '/admin/courses';
    }

    if (courseId && (scope === 'module' || scope === 'topic' || scope === 'lesson')) {
      return `/admin/courses/${encodeURIComponent(courseId)}`;
    }

    return null;
  }

  // ─── Review workflow notifications ──────────────────────────────────────────
  const REVIEW_TYPES = [
    'review_submitted',
    'review_approved',
    'review_changes_requested',
    'review_published',
    'review_discarded',
  ] as const;

  if (REVIEW_TYPES.includes(notification.type as (typeof REVIEW_TYPES)[number])) {
    const metadata = notification.metadata as Record<string, unknown> | null;
    if (!metadata) return null;

    const courseId = metadata.courseId as string | undefined;
    const courseVersionId = metadata.courseVersionId as string | undefined;

    if (!courseId) return null;

    // review_submitted → review detail page (for reviewers)
    if (notification.type === 'review_submitted' && courseVersionId) {
      return `/studio/reviews/${encodeURIComponent(courseId)}/${encodeURIComponent(courseVersionId)}`;
    }

    // All other review types → course editor
    return `/studio/courses/${encodeURIComponent(courseId)}`;
  }

  return null;
}
